import _ from 'lodash';
import { PREDICTION } from '@/tools/prediction/prediction.constants';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { formatNumber, isNumeric } from '@/utilities/numberHelper.utilities';
import type { TableColumn } from '@/core/Table.atom';
import type { ToolItem } from '@/tools/tool.types';

type ModelTableItemData = {
  columns: TableColumn[];
  items: { id: string; [key: string]: any }[];
};

export type ModelTableData = {
  legend: ModelTableItemData;
  coefficients: ModelTableItemData;
  stats: ModelTableItemData;
};

type TranslateLabels = {
  variable: string;
  name: string;
  coefficient: string;
  standardError: string;
  value: string;
  legend: string;
  pValue: string;
};

export interface PredictionModel {
  coefficients: Array<{
    name: string;
    coefficient: number;
    error: number;
    pValue: number;
  }>;
  legend: Array<{
    variable: string;
    name: string;
  }>;
  stats: Array<{
    name: string;
    value: number | string;
  }>;
}

/**
 * Helper function that returns CSV formatted coefficient data as the clipboard text.
 */
export const getCoefficientClipboardText = (model: PredictionModel, translate: TranslateLabels) => {
  if (model.coefficients.length === 0) {
    return '';
  }

  // Add coefficients
  const { name, coefficient, standardError, pValue } = translate;
  let csvTable = `${[name, coefficient, standardError, pValue].join('\t')}\n`;
  _.forEach(model.coefficients, ({ name, coefficient, error, pValue }) => {
    csvTable += `${[name, coefficient, error, pValue].join('\t')}\n`;
  });

  // Add legend
  csvTable += `\n${translate.legend}\n`;
  _.forEach(model.legend, (entry) => {
    csvTable += `${entry.variable} = ${entry.name}\n`;
  });

  return csvTable;
};

/**
 * Helper function that returns CSV formatted statistics data as the clipboard text.
 */
export const getStatisticsClipboardText = (model: PredictionModel, translate: TranslateLabels) => {
  if (model.stats.length === 0) {
    return '';
  }

  const { name, value } = translate;
  let statsCsvTable = `${[name, value].join('\t')}\n`;
  _.forEach(model.stats, ({ name, value }) => {
    statsCsvTable += `${[name, value].join('\t')}\n`;
  });

  return statsCsvTable;
};

const getColumnProps = ({ accessor, header }: Pick<TableColumn, 'accessor' | 'header'>): TableColumn => ({
  accessor,
  header,
  sortable: true,
  cellStyle: {
    maxWidth: '70px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export const getModelTableData = (model: PredictionModel): ModelTableData => ({
  legend: {
    columns: [
      getColumnProps({ accessor: 'variable', header: 'PREDICTION.VARIABLE' }),
      getColumnProps({ accessor: 'name', header: 'PREDICTION.NAME' }),
    ],
    items: _.map(model.legend, (entry, index) => ({
      ...entry,
      id: index as unknown as string,
    })),
  },
  coefficients: {
    columns: [
      getColumnProps({ accessor: 'name', header: 'PREDICTION.NAME' }),
      getColumnProps({ accessor: 'coefficient', header: 'PREDICTION.COEFFICIENT' }),
      getColumnProps({ accessor: 'error', header: 'PREDICTION.STANDARD_ERROR' }),
      getColumnProps({ accessor: 'pValue', header: 'PREDICTION.PVALUE' }),
    ],
    items: _.map(model.coefficients, (entry, index) => ({
      ...entry,
      id: index as unknown as string,
      coefficient: formatNumber(entry.coefficient),
      error: formatNumber(entry.error),
      pValue: formatNumber(entry.pValue),
    })),
  },
  stats: {
    columns: [
      getColumnProps({ accessor: 'name', header: 'PREDICTION.NAME' }),
      getColumnProps({ accessor: 'value', header: 'PREDICTION.VALUE' }),
    ],
    items: _.map(model.stats, (entry, index) => ({
      ...entry,
      id: index as unknown as string,
      value: isNumeric(entry.value) ? formatNumber(entry.value) : entry.value,
    })),
  },
});

export const openTooltipLink = () => window.open(`${SUPPORT_LINK}/prediction`);

export const generateDefaultSortValues = (defaultValue: any) => ({
  legend: defaultValue,
  coefficients: defaultValue,
  stats: defaultValue,
});

/**
 * Helper function that creates the input signal group used in the prediction or prediction model formula
 */
export function createInputsGroupArray(inputSignals: Record<string, any>, option: string, polynomialValue?: number) {
  const keys = _.keys(inputSignals).sort();
  let inputs: string[] = [];
  const cross = [];

  // Linear inputs are always applied
  _.forEach(keys, function (input) {
    inputs.push(`$${input}`);
  });

  if (option === PREDICTION.SCALE.LOG) {
    _.forEach(keys, function (input) {
      inputs.push(`ln($${input}).validValues()`);
    });
  } else if (option === PREDICTION.SCALE.POLYNOMIAL) {
    _.forEach(_.range(2, (polynomialValue ?? 1) + 1), function (p) {
      _.forEach(keys, function (input) {
        inputs.push(`$${input}^${p}`);
      });
    });
  } else if (option === PREDICTION.SCALE.EXPANDED_BASIS) {
    // Polynomial order 3 params
    _.forEach(_.range(2, 3 + 1), function (p) {
      _.forEach(keys, function (input) {
        inputs.push(`$${input}^${p}`);
      });
    });

    // Only apply cross products if there are at least 2 inputs
    if (keys.length >= 2) {
      // Order 2 cross products (e.g. for three inputs: AB, AC, BC)
      _.forEach(keys, function (inputA, i) {
        _.forEach(_.slice(keys, i + 1), function (inputB) {
          cross.push(`$${inputA}*$${inputB}`);
        });
      });

      // Only apply order three cross products if there are 2 or 3 inputs
      if (keys.length <= 3) {
        // Order 3 cross products (e.g. for three inputs: ABC, A2B, A2C, B2A, B2C, C2A, C2B)
        cross.push(
          _.map(keys, function (key) {
            return `$${key}`;
          }).join('*'),
        );
        _.forEach(keys, function (inputA) {
          _.forEach(_.filter(keys, _.partial(_.negate(_.isEqual), inputA)), function (inputB) {
            cross.push(`$${inputA}^2*$${inputB}`);
          });
        });
      }

      inputs = inputs.concat(_.uniq(cross));
    }
  }

  return inputs;
}

/**
 * Adjusts the passed 'principal components to keep' value if necessary (not a number or
 * outside [1, totalInputCount] range).
 *
 * @param inputSignals - An array of the independent signals for the prediction
 * @param option - One of PREDICTION.SCALE (e.g. LINEAR, POLYNOMIAL, LOG, etc.)
 * @param polynomialValue - The polynomial value. Only required when option is POLYNOMIAL.
 * @param principalComponentsToKeep - The number of principal components to keep
 */
export function adjustPrincipalComponentsToKeep(
  inputSignals: ToolItem[],
  option: string,
  polynomialValue: number,
  principalComponentsToKeep: number | string | null | undefined,
) {
  const parsedComponentsToKeep = Number.parseInt(String(principalComponentsToKeep), 10);
  const totalInputs = createInputsGroupArray(inputSignals, option, polynomialValue).length;

  if (!Number.isNaN(parsedComponentsToKeep)) {
    // Make sure variable selection is limited to the [1, totalInputCount] range
    return Math.round(Math.max(Math.min(parsedComponentsToKeep, totalInputs), 1));
  }

  return totalInputs;
}
